import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Close } from "./micro/icons";
import { LanguageContext } from "../contexts/LanguageContext";
import { CountryContext } from "../contexts/CountryContext";
import { DocTypeContext } from "../contexts/DocTypeContext";

type CountryDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
};

type Option = {
    short: string;
    title: string;
};

export const CountryDrawer = ({ isOpen, onClose }: CountryDrawerProps) => {
    const language = useContext(LanguageContext);
    const { country, setCountry } = useContext(CountryContext);
    const { setDocType } = useContext(DocTypeContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [step, setStep] = useState<"countries" | "options">("countries");

    // Reset state when drawer opens
    useEffect(() => {
        if (isOpen) {
            setStep("countries");
            setSelectedCountry(null);
            setSearchTerm("");
        }
    }, [isOpen]);

    const COUNTRY_OPTIONS: { [key: string]: Option[] } = {
        "ad": [{ "short": "ad_printed", "title": language.country_options_printed }],
        "al": [{ "short": "al_printed", "title": language.country_options_printed }],
        "am": [{ "short": "am_printed", "title": language.country_options_printed }],
        "at": [{ "short": "at_printed", "title": language.country_options_printed }],
        "au": [{ "short": "au_printed", "title": language.country_options_printed }],
        "az": [{ "short": "az_printed", "title": language.country_options_printed }],
        "ba": [{ "short": "ba_printed", "title": language.country_options_printed }],
        "be": [{ "short": "be_printed", "title": language.country_options_printed }],
        "bg": [{ "short": "bg_printed", "title": language.country_options_printed }],
        "br": [{ "short": "br_printed", "title": language.country_options_printed }],
        "ch": [{ "short": "ch_printed", "title": language.country_options_printed }],
        "cn": [{ "short": "cn_printed", "title": language.country_options_printed }],
        "cy": [{ "short": "cy_printed", "title": language.country_options_printed }],
        "cz": [{ "short": "cz_printed", "title": language.country_options_printed }],
        "de": [
            { "short": "de_gt10", "title": language.country_options_gt10 },
            { "short": "de_let10", "title": language.country_options_let10 },
        ],
        "dk": [{ "short": "dk_printed", "title": language.country_options_printed }],
        "ee": [{ "short": "ee_printed", "title": language.country_options_printed }],
        "es": [{ "short": "es_printed", "title": language.country_options_printed }],
        "fi": [{ "short": "fi_printed", "title": language.country_options_printed }],
        "fr": [{ "short": "fr_printed", "title": language.country_options_printed }],
        "ge": [{ "short": "ge_printed", "title": language.country_options_printed }],
        "gr": [{ "short": "gr_printed", "title": language.country_options_printed }],
        "hr": [{ "short": "hr_printed", "title": language.country_options_printed }],
        "hu": [{ "short": "hu_printed", "title": language.country_options_printed }],
        "ie": [{ "short": "ie_printed", "title": language.country_options_printed }],
        "il": [{ "short": "il_printed", "title": language.country_options_printed }],
        "in": [
            { "short": "in_printed", "title": language.country_options_printed },
            { "short": "in_oci", "title": language.country_options_oci },
        ],
        "is": [{ "short": "is_printed", "title": language.country_options_printed }],
        "it": [{ "short": "it_printed", "title": language.country_options_printed }],
        "jp": [{ "short": "jp_printed", "title": language.country_options_printed }],
        "kr": [{ "short": "kr_printed", "title": language.country_options_printed }],
        "kz": [{ "short": "kz_printed", "title": language.country_options_printed }],
        "lb": [{ "short": "lb_printed", "title": language.country_options_printed }],
        "li": [{ "short": "li_printed", "title": language.country_options_printed }],
        "lt": [{ "short": "lt_printed", "title": language.country_options_printed }],
        "lu": [{ "short": "lu_printed", "title": language.country_options_printed }],
        "lv": [{ "short": "lv_printed", "title": language.country_options_printed }],
        "mc": [{ "short": "mc_printed", "title": language.country_options_printed }],
        "md": [{ "short": "md_printed", "title": language.country_options_printed }],
        "me": [{ "short": "me_printed", "title": language.country_options_printed }],
        "mk": [{ "short": "mk_printed", "title": language.country_options_printed }],
        "mt": [{ "short": "mt_printed", "title": language.country_options_printed }],
        "ng": [{ "short": "ng_printed", "title": language.country_options_printed }],
        "nl": [
            { "short": "nl_gt10", "title": language.country_options_gt10 },
            { "short": "nl_let10", "title": language.country_options_let10 },
        ],
        "no": [{ "short": "no_printed", "title": language.country_options_printed }],
        "pl": [{ "short": "pl_printed", "title": language.country_options_printed }],
        "pt": [{ "short": "pt_printed", "title": language.country_options_printed }],
        "ro": [{ "short": "ro_printed", "title": language.country_options_printed }],
        "rs": [{ "short": "rs_printed", "title": language.country_options_printed }],
        "sa": [{ "short": "sa_printed", "title": language.country_options_printed }],
        "se": [{ "short": "se_printed", "title": language.country_options_printed }],
        "si": [{ "short": "si_printed", "title": language.country_options_printed }],
        "sk": [{ "short": "sk_printed", "title": language.country_options_printed }],
        "sm": [{ "short": "sm_printed", "title": language.country_options_printed }],
        "th": [{ "short": "th_printed", "title": language.country_options_printed }],
        "tr": [{ "short": "tr_printed", "title": language.country_options_printed }],
        "ua": [{ "short": "ua_printed", "title": language.country_options_printed }],
        "uk": [
            { "short": "uk_gt5", "title": language.country_options_gt5 },
            { "short": "uk_let5", "title": language.country_options_let5 },
        ],
        "us": [{ "short": "us_printed", "title": language.country_options_printed }],
        "xk": [{ "short": "xk_printed", "title": language.country_options_printed }],
        "ae": [{ "short": "ae_printed", "title": language.country_options_printed }],
        "jo": [{ "short": "jo_printed", "title": language.country_options_printed }],
        "id": [{ "short": "id_printed", "title": language.country_options_printed }],
    };

    const countries = [
        { flag: "ad", text: language.countries_ad },
        { flag: "al", text: language.countries_al },
        { flag: "am", text: language.countries_am },
        { flag: "at", text: language.countries_at },
        { flag: "au", text: language.countries_au },
        { flag: "az", text: language.countries_az },
        { flag: "ba", text: language.countries_ba },
        { flag: "be", text: language.countries_be },
        { flag: "bg", text: language.countries_bg },
        { flag: "br", text: language.countries_br },
        { flag: "ch", text: language.countries_ch },
        { flag: "cn", text: language.countries_cn },
        { flag: "cy", text: language.countries_cy },
        { flag: "cz", text: language.countries_cz },
        { flag: "de", text: language.countries_de },
        { flag: "dk", text: language.countries_dk },
        { flag: "ee", text: language.countries_ee },
        { flag: "es", text: language.countries_es },
        { flag: "fi", text: language.countries_fi },
        { flag: "fr", text: language.countries_fr },
        { flag: "gb", text: language.countries_uk, code: "uk" },
        { flag: "ge", text: language.countries_ge },
        { flag: "gr", text: language.countries_gr },
        { flag: "hr", text: language.countries_hr },
        { flag: "hu", text: language.countries_hu },
        { flag: "ie", text: language.countries_ie },
        { flag: "id", text: language.countries_id },
        { flag: "in", text: language.countries_in },
        { flag: "is", text: language.countries_is },
        { flag: "it", text: language.countries_it },
        { flag: "jp", text: language.countries_jp },
        { flag: "kr", text: language.countries_kr },
        { flag: "kz", text: language.countries_kz },
        { flag: "lb", text: language.countries_lb },
        { flag: "li", text: language.countries_li },
        { flag: "lt", text: language.countries_lt },
        { flag: "lu", text: language.countries_lu },
        { flag: "lv", text: language.countries_lv },
        { flag: "mc", text: language.countries_mc },
        { flag: "md", text: language.countries_md },
        { flag: "me", text: language.countries_me },
        { flag: "mk", text: language.countries_mk },
        { flag: "mt", text: language.countries_mt },
        { flag: "ng", text: language.countries_ng },
        { flag: "nl", text: language.countries_nl },
        { flag: "no", text: language.countries_no },
        { flag: "pl", text: language.countries_pl },
        { flag: "pt", text: language.countries_pt },
        { flag: "ro", text: language.countries_ro },
        { flag: "ro", text: language.countries_ro },
        { flag: "sa", text: language.countries_sa },
        { flag: "se", text: language.countries_se },
        { flag: "si", text: language.countries_si },
        { flag: "sk", text: language.countries_sk },
        { flag: "sm", text: language.countries_sm },
        { flag: "th", text: language.countries_th },
        { flag: "tr", text: language.countries_tr },
        { flag: "ua", text: language.countries_ua },
        { flag: "us", text: language.countries_us },
        { flag: "xk", text: language.countries_xk },
        { flag: "il", text: language.countries_il },
        { flag: "ae", text: language.countries_ae },
        { flag: "jo", text: language.countries_jo },
    ];

    const filteredCountries = countries.filter((c) =>
        !searchTerm || c.text?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleCountrySelect = (countryCode: string) => {
        const options = COUNTRY_OPTIONS[countryCode];
        if (!options) return;

        localStorage.setItem("selectedCountry", countryCode);
        setCountry(countryCode);
        setSelectedCountry(countryCode);

        if (options.length === 1) {
            // Single option: select it immediately and close
            finishSelection(options[0].short);
        } else {
            // Multiple options: show the options step
            setStep("options");
        }
    };

    const finishSelection = (docTypeKey: string) => {
        localStorage.setItem("selectedDocType", docTypeKey);
        setDocType(docTypeKey);
        onClose();

        // If on preview page, navigate to upload since specs changed
        if (location.pathname.startsWith("/preview")) {
            navigate("/upload");
        }
    };

    return (
        <div
            className={`fixed inset-0 z-[1020] transition-opacity duration-300 ${
                isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
        >
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className={`absolute right-0 top-0 h-full w-full bg-white overflow-y-auto transform transition-transform duration-300 ${
                isOpen ? "translate-x-0" : "translate-x-full"
            }`}>
                <div className="sticky top-0 z-50 bg-white p-4 flex justify-between items-center border-b">
                    <h2 className="text-lg font-semibold">
                        {step === "countries" ? "Select Country" : "Select Document Type"}
                    </h2>
                    <button onClick={onClose} className="p-2">
                        <Close size={12} />
                    </button>
                </div>

                {step === "countries" && (
                    <div className="flex flex-col w-full items-center justify-center px-6 pt-4">
                        <div className="h-14 w-full mb-5 sticky z-50 top-16">
                            <input
                                className="z-10 absolute mb-2 w-full h-14 p-6 rounded-3xl items-center outline-none border"
                                type="text"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <div className="absolute h-14 w-full flex justify-end items-center pr-5">
                                    <button onClick={() => setSearchTerm("")} className="z-[20] absolute text-primary w-10 h-10">
                                        <Close size={10} />
                                    </button>
                                </div>
                            )}
                        </div>
                        {filteredCountries.map(c => {
                            const code = (c as any).code || c.flag;
                            return (
                                <div
                                    key={c.flag}
                                    onClick={() => handleCountrySelect(code)}
                                    className="mb-2 transition-transform ease-out bg-primary flex justify-between w-full h-20 p-6 rounded-3xl items-center cursor-pointer"
                                >
                                    <span className={`fi fi-${c.flag}`}></span>
                                    <h1 className="text-2xl font-bold text-white">{c.text}</h1>
                                </div>
                            );
                        })}
                        {filteredCountries.length === 0 && <p className="text-grayish">No countries found</p>}
                    </div>
                )}

                {step === "options" && selectedCountry && (
                    <div className="flex flex-col w-full items-center justify-center px-6 pt-4">
                        {COUNTRY_OPTIONS[selectedCountry]?.map((option, index) => (
                            <div
                                key={index}
                                onClick={() => finishSelection(option.short)}
                                className="mb-2 transition-transform ease-out bg-primary flex justify-between w-full h-20 p-6 rounded-3xl items-center cursor-pointer"
                            >
                                <h1 className="text-2xl font-bold text-white">{option.title}</h1>
                            </div>
                        ))}
                        <button
                            onClick={() => setStep("countries")}
                            className="mt-4 text-primary underline"
                        >
                            ← Back to countries
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
